import { useState, useCallback, useEffect } from 'react';
import type { Message, ChatState } from '../types/chat';

// Generate a unique visitor ID
function generateVisitorId(): string {
  const stored = localStorage.getItem('musashi_visitor_id');
  if (stored) return stored;

  const id = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('musashi_visitor_id', id);
  return id;
}

// Generate a unique message ID
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const API_URL = '/api/chat';

export function useChat() {
  const [state, setState] = useState<ChatState>({
    sessionId: null,
    visitorId: '',
    messages: [],
    isOpen: false,
    isLoading: false,
    isScheduling: false,
    availableSlots: [],
    error: null,
    streamingMessage: null,
  });

  // Initialize visitor ID on mount
  useEffect(() => {
    setState(prev => ({
      ...prev,
      visitorId: generateVisitorId(),
    }));
  }, []);

  // Open chat
  const openChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true }));
  }, []);

  // Close chat
  const closeChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Toggle chat
  const toggleChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  // Send message
  const sendMessage = useCallback(async (content: string, language: 'es' | 'en' = 'es') => {
    if (!content.trim() || state.isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: generateMessageId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
    }));

    try {
      // Prepare history for API (exclude the current message)
      const history = state.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: content.trim(),
          history,
          language,
          visitorId: state.visitorId,
          sessionId: state.sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get response');
      }

      // Add assistant message
      const assistantMessage: Message = {
        id: generateMessageId(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        sessionId: data.sessionId || prev.sessionId,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Chat error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Something went wrong',
      }));
    }
  }, [state.messages, state.visitorId, state.sessionId, state.isLoading]);

  // Clear chat history
  const clearChat = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: [],
      sessionId: null,
      error: null,
    }));
  }, []);

  return {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearChat,
  };
}

export default useChat;
